import {
  convertToTransactionBundle,
  DEFAULT_SEARCH_COUNT,
  Filter,
  formatSearchQuery,
  MedplumClient,
  SearchRequest,
  SortRule,
} from '@medplum/core';

/** Custom navigation paths when the user clicks New... */
export const RESOURCE_TYPE_CREATION_PATHS: Partial<Record<any, string>> = {
  Bot: '/admin/bots/new',
  ClientApplication: '/admin/clients/new',
};
export function addSearchValues(search: SearchRequest, config: any | undefined): SearchRequest {
  const resourceType = search.resourceType || getDefaultResourceType(config);
  const fields = search.fields ?? getDefaultFields(resourceType);
  const filters = search.filters ?? (!search.resourceType ? getDefaultFilters(resourceType) : undefined);
  const sortRules = search.sortRules ?? getDefaultSortRules(resourceType);
  const offset = search.offset ?? 0;
  const count = search.count ?? DEFAULT_SEARCH_COUNT;

  return {
    ...search,
    resourceType,
    fields,
    filters,
    sortRules,
    offset,
    count,
  };
}

function getDefaultResourceType(config: any | undefined): string {
  return (
    localStorage.getItem('defaultResourceType') ??
    config?.option?.find((o: any) => o.id === 'defaultResourceType')?.valueString ??
    'Patient'
  );
}

export function getDefaultFields(resourceType: string): string[] {
  const lastSearch = getLastSearch(resourceType);
  if (lastSearch?.fields) {
    return lastSearch.fields;
  }
  const fields = ['id', '_lastUpdated'];
  switch (resourceType) {
    case 'Patient':
      fields.push('name', 'birthDate', 'gender');
      break;
    case 'AccessPolicy':
    case 'Bot':
    case 'ClientApplication':
    case 'Practitioner':
    case 'Project':
    case 'Organization':
    case 'Questionnaire':
    case 'UserConfiguration':
      fields.push('name');
      break;
    case 'CodeSystem':
    case 'ValueSet':
      fields.push('name', 'title', 'status');
      break;
    case 'Condition':
      fields.push('subject', 'code', 'clinicalStatus');
      break;
    case 'Device':
      fields.push('manufacturer', 'deviceName', 'patient');
      break;
    case 'DeviceDefinition':
      fields.push('manufacturer[x]', 'deviceName');
      break;
    case 'DeviceRequest':
      fields.push('code[x]', 'subject');
      break;
    case 'DiagnosticReport':
    case 'Observation':
      fields.push('subject', 'code', 'status');
      break;
    case 'Encounter':
      fields.push('subject');
      break;
    case 'ServiceRequest':
      fields.push('subject', 'code', 'status', 'orderDetail');
      break;
    case 'Subscription':
      fields.push('criteria');
      break;
    case 'User':
      fields.push('email');
      break;
  }
  return fields;
}

function getDefaultFilters(resourceType: string): Filter[] | undefined {
  return getLastSearch(resourceType)?.filters;
}

function getDefaultSortRules(resourceType: string): SortRule[] {
  const lastSearch = getLastSearch(resourceType);
  if (lastSearch?.sortRules) {
    return lastSearch.sortRules;
  }
  return [{ code: '_lastUpdated', descending: true }];
}

function getLastSearch(resourceType: string): SearchRequest | undefined {
  const value = localStorage.getItem(resourceType + '-defaultSearch');
  return value ? (JSON.parse(value) as SearchRequest) : undefined;
}

export function saveLastSearch(search: SearchRequest): void {
  localStorage.setItem('defaultResourceType', search.resourceType);
  localStorage.setItem(search.resourceType + '-defaultSearch', JSON.stringify(search));
}

export async function getTransactionBundle(search: SearchRequest, medplum: MedplumClient): Promise<any> {
  const transactionBundleSearch: SearchRequest = {
    resourceType: search.resourceType,
    count: 1000,
    offset: 0,
    filters: search.filters,
  };
  const transactionBundleSearchValues = addSearchValues(transactionBundleSearch, medplum.getUserConfiguration());
  const bundle = await medplum.search(
    transactionBundleSearchValues.resourceType as any,
    formatSearchQuery({ ...transactionBundleSearchValues, total: 'accurate', fields: undefined })
  );
  return convertToTransactionBundle(bundle);
}

interface Extension {
  url: string;
  valueString?: string;
  valueInteger?: number;
  extension: any
}

interface Meta {
  project?: string;
  versionId?: string;
  lastUpdated?: string;
  author?: { reference: string; display: string };
  compartment?: { reference: string }[];
  extension?: Extension[];
}

interface ClientApplication {
  meta: Meta;
  resourceType: string;
  name: string;
  secret: string;
  description: string;
  redirectUri: string;
  id: string;
}

export function getExtensionValues<T extends Extension>(meta: Meta, url: string): Record<string, string | number> {
  const extensionValues: Record<string, string | number> = {};

  if (meta?.extension) {
      for (const ext of meta.extension) {
          if (ext.url === url && ext.extension) {
              for (const subExt of ext.extension) {
                  if (subExt.valueString !== undefined) {
                      extensionValues[subExt.url] = subExt.valueString;
                  } else if (subExt.valueInteger !== undefined) {
                      extensionValues[subExt.url] = subExt.valueInteger;
                  }
              }
          }
      }
  }

  return extensionValues;
}

export interface ExtensionObject {
  url: string;
  extension: { url: string; valueString: string }[];
}

interface TokensAndUrl {
  [clientId: string]: {
      accessToken: string;
      refreshToken: string | null;
      clientId: string;
      name: string;
  };
}

export function getTokensAndUrl(extensionObject: ExtensionObject[], desiredIntegrationType: string): TokensAndUrl {
  const tokensAndUrl: TokensAndUrl = {};
  
  extensionObject.forEach(app => {
      const clientId = app.url.split('/').pop()!;
      const integrationTypeObj = app.extension.find(ext => ext.url === 'integrationType');
      const accessTokenObj = app.extension.find(ext => ext.url === 'access_token');
      const refreshTokenObj = app.extension.find(ext => ext.url === 'refresh_token');
      const name = app.extension.find(ext => ext.url === 'display');
      
      if (integrationTypeObj && integrationTypeObj.valueString === desiredIntegrationType && accessTokenObj) {
          tokensAndUrl[desiredIntegrationType] = {
              accessToken: accessTokenObj.valueString,
              refreshToken: refreshTokenObj?.valueString || null,
              name: name?.valueString??'provider name',
              clientId: clientId
          };
      }
  });
  
  return tokensAndUrl;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
          if (typeof reader.result === 'string') {
              resolve(reader.result);
          } else {
              reject(new Error('Failed to read file as base64.'));
          }
      };

      reader.onerror = () => {
          reject(reader.error);
      };

      reader.readAsDataURL(file);
  });
}


